// `/chat/people` (CHAT-DM, Slack's People): everyone in the company, people and digital
// employees, each labelled, with search and a People / Digital employees filter. A row shows the
// avatar, name, role or seat and status; "Message" opens (or reopens) the DM through the same
// path as the profile popover, and "View profile" opens that popover. Not for the viewer's own
// row (no DM with yourself), an inactive person or a retired DE.
import { AvatarTile, Button, SearchField, WorkerBadge, workerName } from "@companyos/ui";
import { UsersIcon } from "lucide-react";
import { useState } from "react";
import { Loading } from "../screen-parts";
import type { ChatMember, MemberIndex } from "./chat-model";
import { ChatPageBar } from "./chat-pages";
import { canMessage, dmBlockedBy, MemberPopover, MessageButton } from "./member-card";
import { localTime, StatusEmoji } from "./member-status";
import { PRESENCE_LABEL, PresenceDot, type PresenceState, presenceState } from "./presence-dot";
import { type ChatContext, useChat, usePresence } from "./use-chat";

export type PeopleFilter = "all" | "people" | "des";

const DE_STATUS: Record<NonNullable<ChatMember["deStatus"]>, string> = {
  onboarding: "Onboarding",
  active: "Active",
  paused: "Paused",
  retired: "Retired",
  not_deployed: "Not deployed",
};

/**
 * The directory's rows: every member but a retired DE, people first, then DEs, each by name;
 * `query` matches name, role or seat; `filter` keeps people or DEs.
 */
export function directoryOf(
  members: MemberIndex,
  query: string,
  filter: PeopleFilter,
): ChatMember[] {
  const q = query.trim().toLowerCase();
  return [...members.values()]
    .filter((m) => m.deStatus !== "retired")
    .filter((m) => filter === "all" || (filter === "des") === (m.kind === "de"))
    .filter(
      (m) =>
        !q ||
        m.name.toLowerCase().includes(q) ||
        (m.seat ?? "").toLowerCase().includes(q) ||
        (m.role ?? "").toLowerCase().includes(q),
    )
    .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === "de" ? 1 : -1));
}

const FILTERS: { value: PeopleFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "people", label: "People" },
  { value: "des", label: "Digital employees" },
];

export function PeoplePage() {
  const chat = useChat();
  const present = usePresence(chat.api, chat.co);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<PeopleFilter>("all");
  const rows = directoryOf(chat.members, q, filter);
  const failed = chat.peopleDirectory.failed || chat.deDirectory.failed;
  const loading = !failed && (!chat.peopleDirectory.complete || !chat.deDirectory.complete);
  return (
    <div data-page="chat-people" className="flex h-full min-h-0 flex-col">
      <ChatPageBar title="People">
        <SearchField
          aria-label="Search people"
          placeholder="Search people"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-8 w-56 max-md:w-36"
        />
      </ChatPageBar>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2 max-md:px-1">
        <fieldset
          aria-label="Show"
          className="m-0 mb-2 flex flex-wrap gap-1 border-0 p-0 px-2"
          data-slot="people-filter"
        >
          {FILTERS.map((f) => (
            <Button
              key={f.value}
              type="button"
              size="xs"
              variant={filter === f.value ? "secondary" : "ghost"}
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </fieldset>
        {failed ? (
          <p
            role="alert"
            className="m-0 flex items-center gap-2 px-2 py-1 text-sm text-danger-text"
          >
            Some members could not be loaded.
            <Button
              type="button"
              variant="link"
              size="xs"
              className="h-5 px-0"
              onClick={() => {
                if (chat.peopleDirectory.failed) chat.peopleDirectory.retry();
                if (chat.deDirectory.failed) chat.deDirectory.retry();
              }}
            >
              Retry
            </Button>
          </p>
        ) : null}
        {loading && !rows.length ? <Loading label="Loading people" /> : null}
        {!loading && !rows.length ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            {q.trim() ? "No one matches." : "No one here yet."}
          </p>
        ) : null}
        <ul aria-label="People" className="m-0 flex list-none flex-col p-0" data-slot="people-list">
          {rows.map((m) => (
            <PersonRow key={m.id} chat={chat} member={m} online={present.has(m.id)} />
          ))}
        </ul>
      </div>
    </div>
  );
}

function PersonRow({
  chat,
  member: m,
  online,
}: {
  chat: ChatContext;
  member: ChatMember;
  online: boolean;
}) {
  const de = m.kind === "de";
  const name = de ? workerName(m.name) : m.name;
  const me = m.id === chat.meId;
  const detail = de
    ? (m.seat?.replace(/\s*\(DE\)$/, "") ?? "Digital employee")
    : (m.role ?? "Person");
  // CHAT-SWITCH-B: a person's presence, custom status and local time (CHAT-SWITCH-A's statuses);
  // the presence socket's "here" while the statuses have not come in.
  const known = de ? undefined : chat.statuses?.get(m.id);
  const state: PresenceState | null =
    de || m.inactive ? null : known ? presenceState(known) : online ? "active" : "away";
  const status = de
    ? DE_STATUS[m.deStatus ?? "active"]
    : m.inactive
      ? "Inactive"
      : PRESENCE_LABEL[state ?? "away"];
  const zone = known?.time_zone ?? null;
  const time = zone ? localTime(zone, new Date()) : null;
  // No DM with yourself; a paused or inactive member's Message shows disabled, with why.
  const messageable = !me && canMessage(chat);
  return (
    <li
      data-person-row={m.id}
      className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-sunken"
    >
      <span className="relative inline-flex shrink-0">
        <AvatarTile name={name} kind={de ? "agent" : "person"} size="sm" aria-hidden="true" />
        {state ? (
          <PresenceDot state={state} className="absolute -right-0.5 -bottom-0.5 ring-card" />
        ) : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground">
          <span className="truncate">{name}</span>
          {de ? <WorkerBadge kind="de" size="sm" /> : null}
          <StatusEmoji status={known} />
          {me ? <span className="text-xs font-normal text-muted-foreground">(you)</span> : null}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {detail} · <span data-slot="person-status">{status}</span>
          {known?.text ? <span data-slot="person-status-text"> · {known.text}</span> : null}
          {time ? (
            <span data-slot="person-local-time" title={zone ?? undefined}>
              {" "}
              · {time} local
            </span>
          ) : null}
        </span>
      </span>
      <MemberPopover
        chat={chat}
        memberId={m.id}
        trigger={
          <Button type="button" size="xs" variant="ghost" aria-label={`View profile: ${name}`}>
            <UsersIcon data-icon="inline-start" />
            View profile
          </Button>
        }
      />
      {messageable ? (
        <MessageButton
          chat={chat}
          memberId={m.id}
          name={name}
          blocked={dmBlockedBy(m)}
          size="xs"
          variant="outline"
        />
      ) : null}
    </li>
  );
}
